import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSetLegacyFrameActive } from "@/contexts/LegacyFrameContext";
import { LEGACY_FRAME_STATE_KEY, toSameOriginPath, withoutShellBypass } from "@/utils/legacyShell";
import { Spinner } from "@/components/common/Loading/Spinner";

export const LEGACY_LOADED_MESSAGE = "vecta:legacy-loaded";
export const LEGACY_RELOAD_FAVOURITES_MESSAGE = "vecta:reloadFavourites";

type LegacyMessage = { type?: string; url?: string; title?: string };

const reloadFavourites = () => {
  const reload = (window as unknown as { reloadFavouritePanel?: () => void }).reloadFavouritePanel;
  if (typeof reload === "function") reload();
};

const readFrameLocation = (frame: HTMLIFrameElement): { path: string; title: string } | null => {
  try {
    const frameWindow = frame.contentWindow;
    if (!frameWindow || frameWindow.location.origin !== window.location.origin) return null;
    if (frameWindow.location.href === "about:blank") return null;
    const { pathname, search, hash } = frameWindow.location;
    return { path: pathname + search + hash, title: frameWindow.document.title };
  } catch {
    return null;
  }
};

export const LegacyFrame = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const setLegacyActive = useSetLegacyFrameActive();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [initialSrc] = useState(() => window.location.pathname + window.location.search + window.location.hash);
  const frameUrlRef = useRef(initialSrc);

  useLayoutEffect(() => {
    setLegacyActive(true);
    return () => setLegacyActive(false);
  }, [setLegacyActive]);

  const syncFromFrame = useCallback((framePath: string, title: string) => {
    if (title) document.title = title;
    const path = withoutShellBypass(framePath);
    if (path === frameUrlRef.current) return;
    frameUrlRef.current = path;
    navigate(path, { replace: true, state: { [LEGACY_FRAME_STATE_KEY]: true } });
  }, [navigate]);

  useEffect(() => {
    const target = location.pathname + location.search + location.hash;
    if (target === frameUrlRef.current) return;
    const frame = frameRef.current;
    if (!frame) return;
    frameUrlRef.current = target;
    setLoaded(false);
    try {
      frame.contentWindow?.location.replace(target);
    } catch {
      frame.src = target;
    }
  }, [location.pathname, location.search, location.hash]);

  const handleLoad = () => {
    setLoaded(true);
    if (!frameRef.current) return;
    const current = readFrameLocation(frameRef.current);
    if (current) syncFromFrame(current.path, current.title);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent<LegacyMessage>) => {
      const frame = frameRef.current;
      if (!frame || event.origin !== window.location.origin || event.source !== frame.contentWindow) return;
      const message = event.data;
      if (!message || typeof message !== "object") return;
      if (message.type === LEGACY_RELOAD_FAVOURITES_MESSAGE) {
        reloadFavourites();
        return;
      }
      if (message.type !== LEGACY_LOADED_MESSAGE) return;
      const current = readFrameLocation(frame);
      const path = current?.path ?? (typeof message.url === "string" ? toSameOriginPath(message.url) : null);
      if (!path) return;
      const title = typeof message.title === "string" && message.title ? message.title : current?.title ?? "";
      syncFromFrame(path, title);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [syncFromFrame]);

  return (
    <div className="relative flex flex-1 flex-col">
      {!loaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white" data-testid="legacy-frame-loading">
          <Spinner />
        </div>
      )}
      <iframe
        ref={frameRef}
        title="Page content"
        src={initialSrc}
        onLoad={handleLoad}
        className="block w-full flex-1 border-0"
        data-testid="legacy-frame"
      />
    </div>
  );
};
